import React, { FC, HTMLAttributes, useReducer, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { makeStyles } from 'tss-react/mui';
import { TextField, FormControl, Select, Button, Dialog, IconButton } from '@mui/material';
import { IncentiveFieldType, AwardOperatorType, IncentiveRequirementSet } from '../../../types/incentive-types';
import { map, forEach } from 'lodash';
import { useUpdateEffect } from 'src/app/common/utils';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

type AwardRequirementCardProps = {
  initialSet?: IncentiveRequirementSet[];
  onCancel: () => void;
  onConfirm: (requirementSet?: IncentiveRequirementSet[]) => void;
};

const useStyles = makeStyles()((theme) => ({
  confirmCard: {
    width: 200,
    padding: 20
  },
  main: {
    width: '30vw',
    padding: 10
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 15
  },
  header: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
    lineHeight: 1.5
  },
  footerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: 10
  },
  subText: {
    fontSize: '1rem',
    fontWeight: 'normal',
    lineHeight: 1
  },
  subHeader: {
    fontSize: '1rem',
    lineHeight: 1.2
  },
  rowContainer: {
    display: 'flex'
  },
  requirementRow: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  inputBox: {
    paddingTop: 8,
    paddingBottom: 8
  },
  btnContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center'
  },
  closeBtn: {
    textDecoration: 'underline',
    color: 'blue',
    cursor: 'pointer',
    marginLeft: 10
  }
}));

type AwardRequirementState = {
  requirements: IncentiveRequirementSet[];
};

type AddRequirementRowAction = {
  type: "ADD_REQUIREMENT_ROW";
};

type MoveRequirementRowAction = {
  type: "MOVE_REQUIREMENT_ROW";
  payload: {
    from: number;
    to: number;
  }
}

type DeleteRequirementRowAction = {
  type: "DELETE_REQUIREMENT_ROW";
  payload: {
    index: number;
  };
};

type ModifyRequirementRowAction = {
  type: "MODIFY_REQUIREMENT_ROW";
  payload: {
    index: number;
    field: keyof IncentiveRequirementSet;
    value: string;
  };
};

type ResetRequirementAction = {
  type: "RESET_REQUIREMENT"
}

type AwardRequirementAction = AddRequirementRowAction | MoveRequirementRowAction |
  ModifyRequirementRowAction | DeleteRequirementRowAction | ResetRequirementAction;

const awardRequirementReducer = (state: AwardRequirementState, action: AwardRequirementAction) => {
  switch (action.type) {
    case 'ADD_REQUIREMENT_ROW':
      return {
        ...state,
        requirements: [...state.requirements, {
          setId: String(state.requirements.length),
          operand: '',
          operator: '',
          type: ''
        }]
      }
    case 'MOVE_REQUIREMENT_ROW':
      let requirements = state.requirements;
      requirements.splice(action.payload.to, 0, requirements.splice(action.payload.from, 1)[0]);
      return { ...state };
    case 'MODIFY_REQUIREMENT_ROW':
      let correspodingRequirement = state.requirements[action.payload.index];
      correspodingRequirement[action.payload.field] = action.payload.value;
      return { ...state };
    case 'DELETE_REQUIREMENT_ROW':
      let newRequirements = [...state.requirements];
      newRequirements.splice(action.payload.index, 1);
      return {
        ...state,
        requirements: newRequirements
      };
    case 'RESET_REQUIREMENT':
      return {
        ...state,
        requirements: []
      };
    default:
      return state;
  };
};

type ErrorItem = {
  index: number;
  field: keyof IncentiveRequirementSet;
};

type DialogState = {
  isOpen: boolean;
  onConfirm: () => void;
};

const AwardRequirementCard: FC<AwardRequirementCardProps & HTMLAttributes<HTMLDivElement>> = ({
  initialSet,
  onCancel,
  onConfirm,
  ...rest
}) => {

  const { classes } = useStyles();

  const [awardRequirementState, awardRequirementDispatch] = useReducer(
    awardRequirementReducer, initialSet ? { requirements: initialSet } : {
      requirements: [{
        setId: '0',
        operand: '',
        operator: '',
        type: ''
      }]
    });
  const [errorState, setErrorState] = useState<ErrorItem[]>([]);
  const [dialogState, setDialogState] = useState<DialogState>({
    isOpen: false,
    onConfirm: onCancel
  });

  const errorValidator = () => {
    const requirements = awardRequirementState.requirements;
    let tempErrorState: ErrorItem[] = [];
    requirements.forEach((requirement, index) => {
      forEach(requirement, (value, key) => {
        if (!value) {
          tempErrorState.push({
            index,
            field: key as keyof IncentiveRequirementSet
          });
        }
      });
    });
    setErrorState(tempErrorState);
    return tempErrorState.length === 0;
  };

  const isPromptWarning = () => {
    let flag = false;
    const requirements = awardRequirementState.requirements;
    requirements.forEach((requirement, index) => {
      forEach(requirement, (value, key) => {
        if (key !== 'setId' && value) {
          flag = true;
        }
      });
    });
    return flag;
  }

  useUpdateEffect(() => {
    const requirements = awardRequirementState.requirements;
    let tempErrorState = [...errorState];
    requirements.forEach((requirement, index) => {
      forEach(requirement, (value, key) => {
        if (value) {
          let deleteIndex = tempErrorState.findIndex(errorItem => errorItem.index === index && errorItem.field === key);
          if (deleteIndex !== -1) {
            tempErrorState.splice(deleteIndex, 1);
          }
        }
      });
    });
    setErrorState(tempErrorState);
  }, [awardRequirementState]);

  return (
    <>
      <Dialog onClose={() => setDialogState({ ...dialogState, isOpen: false })} open={dialogState.isOpen}>
        <div className={classes.confirmCard}>
          Are you sure you want to leave?
          <div className={classes.btnContainer}>
            <div className={classes.closeBtn} onClick={onCancel}>Yes</div>
            <div className={classes.closeBtn} onClick={() => setDialogState({ ...dialogState, isOpen: false })}>No</div>
          </div>
        </div>
      </Dialog>
      <div className={classes.main} {...rest}>
        <div className={classes.headerContainer}>
          <span className={classes.header}>Requirement</span>
          <IconButton
            style={{ padding: 5, marginLeft: 10 }}
            onClick={() => awardRequirementDispatch({ type: 'ADD_REQUIREMENT_ROW' })}
          >
            <AddIcon
              style={{
                color: 'black',
                cursor: 'pointer'
              }}
            />
          </IconButton>
        </div>
        {awardRequirementState.requirements.length > 0 &&
          <div className={classes.requirementRow}>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <td><span className={classes.subHeader}>KPI</span></td>
                  <td><span className={classes.subHeader}>Condition</span></td>
                  <td><span className={classes.subHeader}>Type</span></td>
                  <td></td>
                </tr>
                {awardRequirementState.requirements.map((item, index) => (
                  <tr key={`add-requirement-row-${index}`}>
                    <td style={{ width: '33%' }}>
                      <TextField
                        error={!!errorState.find(errorItem => errorItem.index === index && errorItem.field === 'operand')}
                        style={{ width: '90%' }}
                        InputProps={{
                          classes: {
                            input: classes.inputBox,
                          },
                        }}
                        margin="dense"
                        variant="outlined"
                        value={item.operand}
                        onChange={e => awardRequirementDispatch({
                          type: 'MODIFY_REQUIREMENT_ROW',
                          payload: {
                            index,
                            field: 'operand',
                            value: e.target.value
                          }
                        })}
                      />
                    </td>
                    <td style={{ width: '33%' }}>
                      <FormControl
                        error={!!errorState.find(errorItem => errorItem.index === index && errorItem.field === 'operator')}
                        margin="dense"
                        variant="outlined"
                        fullWidth
                        style={{ width: '90%' }}
                      >
                        <Select
                          native
                          value={item.operator}
                          inputProps={{
                            classes: {
                              root: classes.inputBox,
                            },
                          }}
                          onChange={e => awardRequirementDispatch({
                            type: 'MODIFY_REQUIREMENT_ROW',
                            payload: {
                              index,
                              field: 'operator',
                              value: e.target.value as string || ''
                            }
                          })}
                        >
                          <option aria-label="None" value="" />
                          {map(AwardOperatorType, (option: string) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </td>
                    <td style={{ width: '34%' }}>
                      <FormControl
                        error={!!errorState.find(errorItem => errorItem.index === index && errorItem.field === 'type')}
                        margin="dense"
                        variant="outlined"
                        fullWidth
                        style={{ width: '90%' }}
                      >
                        <Select
                          native
                          value={item.type}
                          inputProps={{
                            classes: {
                              root: classes.inputBox,
                            },
                          }}
                          onChange={e => awardRequirementDispatch({
                            type: 'MODIFY_REQUIREMENT_ROW',
                            payload: {
                              index,
                              field: 'type',
                              value: e.target.value as string || ''
                            }
                          })}
                        >
                          <option aria-label="None" value="" />
                          {map(IncentiveFieldType, (option: string) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </td>
                    <td align="right">
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%'}}>
                        <IconButton
                          style={{ padding: 5 }}
                          onClick={() => awardRequirementState.requirements[index + 1] && awardRequirementDispatch({ type: 'MOVE_REQUIREMENT_ROW', payload: { from: index, to: index + 1 } })}
                          disabled={!!!awardRequirementState.requirements[index + 1]}
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                        <IconButton
                          style={{ padding: 5 }}
                          onClick={() => awardRequirementState.requirements[index - 1] && awardRequirementDispatch({ type: 'MOVE_REQUIREMENT_ROW', payload: { from: index, to: index - 1 } })}
                          disabled={!!!awardRequirementState.requirements[index - 1]}
                        >
                          <ArrowDropUpIcon />
                        </IconButton>
                        <IconButton
                          style={{ padding: 5 }}
                          onClick={() => awardRequirementDispatch({ type: 'DELETE_REQUIREMENT_ROW', payload: { index }})}
                        >
                          <HighlightOffIcon />
                        </IconButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        }
        <div className={classes.footerContainer}>
          <div className={classes.rowContainer}>
            <Button
              style={{ marginLeft: 15 }}
              variant="contained"
              onClick={() => isPromptWarning() ? setDialogState({ ...dialogState, isOpen: true }) : onCancel()}
            >
              Cancel
          </Button>
            <Button
              style={{ marginLeft: 15 }}
              variant="contained"
              color="secondary"
              onClick={() => {
                if (errorValidator()) {
                  onConfirm(awardRequirementState.requirements);
                }
              }}
            >
              Confirm
          </Button>
          </div>
        </div>
      </div>
    </>
  )
};

export default AwardRequirementCard;